<template>
    <div class="flex w-full flex-col flex-1 grow" @click="fireTag">
        <Toggler />
        <div class="flex rounded mt-6 bg-white flex-col flex-1">
            <DonationAmount
                v-show="showDonationAmountStep"
                :loading="loadingData"
                :organization-id="organizationId"
            />

            <BillingInfo
                v-show="showBillingDetailsStep"
            />

            <PaymentDetails
                v-show="showPaymentDetailsStep"
                :donor-type="TYPE_COMMUNITY"
                @set-error="error = $event"
            />

            <Success
                v-if="showSuccessStep"
            />

            <Error
                v-if="showErrorStep"
                :error="error"
            />

        </div>
    </div>
</template>

<script setup>
import Toggler from "@/components/Toggler.vue";
import {computed, onBeforeMount, onMounted, ref} from "vue";
import DonationAmount from "@/views/Steps/DonationAmount.vue";
import PaymentDetails from "@/views/Steps/PaymentDetails.vue";
import BillingInfo from "@/views/Steps/BillingInfo.vue";
import {useAPI} from "@/composables/useAPI";
import Success from "@/views/Steps/Success.vue";
import Error from "@/views/Steps/Error.vue";
import {useDonationStore} from "@/store/useDonationStore";
import {storeToRefs} from "pinia";

const {
    medallionFlag,
    predefinedDonations,
    selectedDonation,
    currentStepIndex,
    selectedDonationFrequency,
    organizationSlug,
    donationFrequencies,
    isDonationCompleted,
    error,
} = storeToRefs(useDonationStore());

const { withMedallionGift } = useDonationStore();

const { getDonationValues } = useAPI();

const steps = ['DONATION_AMOUNT', 'BILLING_DETAILS', 'PAYMENT_DETAILS'];

const params = new URLSearchParams(window.location.search);
const organizationId = params.get('org') || 0;

const TYPE_COMMUNITY = 0;
const TYPE_BUSINESS = 1;

const loadingData = ref(false);

async function loadDonationValues() {
    loadingData.value = true;
    try {
        const req = await getDonationValues(organizationId);
        predefinedDonations.value = req.predefinedDonations
            .filter((donation) => donation.type === 'community')
            .map((donation) => {
                return {
                    ...donation,
                    isCustomDonation: false,
                }
            });

        selectedDonation.value = predefinedDonations.value[0];
        organizationSlug.value = req.client.url_slug;
        donationFrequencies.value = req.donation_frequencies.splice(0, 2);
        selectedDonationFrequency.value = donationFrequencies.value[1];
    } catch (e) {
        error.value = 'An error occurred while loading the widget. Please try again later. Error: ' + e;
    }

    loadingData.value = false;
}

function insertGAScript() {
    const gaTrackingId = process.env.VUE_APP_GOOGLE_ANALYTICS_ID;
    const scriptUrl = 'https://www.googletagmanager.com/gtag/js?id=' + gaTrackingId;
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = scriptUrl;
    document.head.appendChild(gaScript);

    gaScript.onload = () => {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
            dataLayer.push(arguments);
        }
        window.gtag('js', new Date());
        window.gtag('config', gaTrackingId, {
            'cookie_domain': 'auto', // or specify your domain
            'cookie_prefix': '_ga', // default is _ga
            'cookie_update': true, // default
            'cookieFlags': 'SameSite=None;Secure', // for cross-domain cookies
            'cookie_flags': 'SameSite=None;Secure', // for cross-domain cookies
            'linker': {
                'accept_incoming': true,
            }
        });
    };
}

const showDonationAmountStep = computed(() => {
    return steps[currentStepIndex.value] === 'DONATION_AMOUNT' && !isDonationCompleted.value && !error.value;
})

const showBillingDetailsStep = computed(() => {
    return steps[currentStepIndex.value] === 'BILLING_DETAILS' && !isDonationCompleted.value && !error.value;
})

const showPaymentDetailsStep = computed(() => {
    return steps[currentStepIndex.value] === 'PAYMENT_DETAILS' && !isDonationCompleted.value && !error.value;
})

const showSuccessStep = computed(() => {
    return isDonationCompleted.value && !error.value;
})

const showErrorStep = computed(() => {
    return error.value;
})

onBeforeMount(() => {
    let color = '#D56000';
    if(params.get('color')) {
        color = decodeURIComponent(params.get('color'))
    }
    document.body.style.setProperty('--primary-color', color);
})

onBeforeMount(() => {
    const param = parseInt(params.get('medallion'));
    medallionFlag.value = typeof param === 'number' && !isNaN(param) ? param : 0;
})

onMounted( () => {
    if(!organizationId) {
        error.value = 'Organization ID is missing';
        return;
    }

    console.log(medallionFlag.value);

    if(medallionFlag.value === null) {
        error.value = 'Medallion flag is missing';
        return;
    }

    loadDonationValues();

    insertGAScript();
})
</script>